import axios, { AxiosRequestConfig, AxiosResponse } from 'axios'
import stringify from 'json-stable-stringify'
import isEmpty from 'lodash.isempty'
import {
  BehaviorSubject,
  combineLatest,
  from,
  merge,
  Observable,
  Subject,
  Subscription,
  timer,
} from 'rxjs'
import { flatMap, map, share } from 'rxjs/operators'
import { Ref, ref } from 'vue'

import {
  TransientBaseObject,
  TransientBaseObjectType,
} from '@/models/core/base'
import { getModelClass } from '@/models/objectRegistry'
import store from '@/store'
import { DataStateDisplay } from '@/util/DataStateDisplay'
import { insertPropsInObject, simpleCopy } from '@/util/util'

export const BASE_URL = '/api/v1/'

/**
 * Construct url from objectType
 * @returns {string}
 */
function constructUrl(model: TransientBaseObjectType): string {
  let url = BASE_URL + model.apiUrl
  if (!model.noTrailingSlash) {
    url += '/'
  }
  return url
}

function constructDeleteUrl(
  modelClass: TransientBaseObjectType,
  id: string,
  purge: boolean = false
) {
  let url = constructUrl(modelClass) + id + '/'
  if (purge) {
    url = url + '?purge=true'
  }
  return url
}

function constructUpdateUrl(
  modelClass: TransientBaseObjectType,
  id: string,
  data?: ModelClass
) {
  if (modelClass.customUpdateUrl) {
    return BASE_URL + modelClass.customUpdateUrl(data)
  }
  return constructUrl(modelClass) + id + '/'
}

function constructCreateUrl(
  modelClass: TransientBaseObjectType,
  data?: TransientBaseObjectType
) {
  if (modelClass.customCreateUrl) {
    return BASE_URL + modelClass.customCreateUrl(data)
  }
  return constructUrl(modelClass)
}

function constructFindUrl(modelClass: TransientBaseObjectType) {
  return constructUrl(modelClass) + 'find?'
}

function constructGetUrl(modelClass: TransientBaseObjectType, id: string) {
  let url = constructUrl(modelClass) + id
  if (!modelClass.noTrailingSlash) {
    url += '/'
  }
  return url
}

/**
 * Constructs url based on filter and pagination settings
 * @returns {string}
 */
function constructListUrl(url: string, context: Context): string {
  let params = '?'
  if (context.filter) {
    for (const key in context.filter) {
      if (context.filter[key] !== undefined && context.filter[key] !== null) {
        if (Array.isArray(context.filter[key])) {
          for (let i = 0; i < context.filter[key].length; i++) {
            params +=
              key + '=' + encodeURIComponent(context.filter[key][i]) + '&'
          }
        } else {
          params += key + '=' + encodeURIComponent(context.filter[key]) + '&'
        }
      }
    }
  }
  if (
    context.pagination !== undefined &&
    context.pagination !== null &&
    context.pagination.page !== undefined
  ) {
    params += 'page=' + context.pagination.page
    if (context.pagination.pageSize !== undefined) {
      params += '&page_size=' + context.pagination.pageSize
    }
    params += '&show_size=true'
  } else {
    params = params.slice(0, params.length - 1)
  }
  return url + params
}

export namespace Collection {
  export interface AnnotationResponse<T> {
    id: string
    annotations: {
      [key: string]: T | T[]
    }
  }

  // An annotation is a simple transformation of a field or an additional
  // field which only needs the object itself
  export interface Annotation<T, K> {
    key: string
    newKey?: string // If not present, modify key in place
    callback: (
      object: T,
      apiClient: ApiClientV2
    ) => Promise<Collection.AnnotationResponse<K>>
  }

  export interface RelatedAnnotation<T> {
    // Model class of related object
    relatedModelClass?: TransientBaseObjectType
    // The property name of the related object on own object in case its not the same as the object type name
    relatedObjectType?: string
    // Property name of related object that should be used displayed
    relatedObjectProperty: string
  }

  export interface Filter {
    order_by?: string
    [K: string]: any
  }

  export interface Pagination {
    size?: number
    page?: number
    pageSize?: number
  }

  export interface Ordering {
    field: string
    order?: 'asc' | 'desc'
  }

  export interface Status {
    fetching: boolean // true when a request is underway
    ready: boolean // true when first request is finished
    readyPromise: Promise<boolean>
  }

  export interface Info {
    size?: number
  }

  export interface HookOption {
    [key: string]: any
  }

  export interface HookOptions {
    beforeSaveOptions?: HookOption
    afterSaveOptions?: HookOption
  }
}

export class Collection<T, K> {
  modelClass: TransientBaseObjectType
  subscriptionsMap = new Map<string, ApiListSubscription<K>>()

  /**
   * Create new Collection.
   * @param {TransientBaseObject} modelClass
   */
  constructor(modelClass: TransientBaseObjectType) {
    // Check if modelClass has defined all needed properties
    if (modelClass.apiUrl === undefined) {
      throw new Error(
        `ModelClass ${modelClass.objectType} needs to define property "apiUrl".`
      )
    }
    if (modelClass.defaultModel === undefined) {
      throw new Error(
        `ModelClass ${modelClass.objectType} needs to define "defaultModel".`
      )
    }
    this.modelClass = modelClass
  }

  /**
   * Create new ApiListSubscription
   */
  subscribe(
    config: AxiosRequestConfig,
    modelClass: TransientBaseObjectType,
    filter: Collection.Filter,
    pagination: Collection.Pagination,
    annotations?: Collection.Annotation<TransientBaseObject, K>[],
    joins?: Collection.RelatedAnnotation<TransientBaseObject>[],
    refreshPeriod?: number
  ): ApiListSubscription<K> {
    const apiListSubscription = new ApiListSubscription(
      config,
      modelClass,
      filter,
      pagination,
      annotations,
      joins,
      refreshPeriod
    )
    this.subscriptionsMap.set(apiListSubscription.key, apiListSubscription)
    return apiListSubscription
  }

  public get<T>(
    id: string,
    config: AxiosRequestConfig = store.state.global.axiosConfig
  ): Promise<T> {
    const url = constructGetUrl(this.modelClass, id)
    return axios
      .get(url, config)
      .then((response) => {
        return response.data
      })
      .catch((error) => {
        console.warn(error)
        return Promise.reject(error)
      })
  }

  public getList<T>(
    context: Context,
    config: AxiosRequestConfig = store.state.global.axiosConfig
  ): Promise<ListResponse<T>> {
    if (context.filter === null) {
      return Promise.resolve({
        size: 0,
        page: 1,
        page_size: 1,
        results: [],
      })
    }
    const url = constructListUrl(constructUrl(this.modelClass), context)
    return axios
      .get(url, config)
      .then((response) => {
        return response.data
      })
      .catch((error) => {
        console.warn(error)
        return Promise.reject(error)
      })
  }

  /**
   * Create new object and refresh subscribers
   * @param data
   * @param config for axios request
   * @returns {Promise<never | any[]>}
   */
  public async create(
    data: ModelClass,
    config: AxiosRequestConfig = store.state.global.axiosConfig,
    isUpdate: boolean = false,
    hookOptions: Collection.HookOptions = {
      beforeSaveOptions: undefined,
      afterSaveOptions: undefined,
    }
  ): Promise<AxiosResponse<K>> {
    const axiosConfig: AxiosRequestConfig = JSON.parse(JSON.stringify(config))
    let response: AxiosResponse<K>
    if (this.modelClass.beforeSaveHook) {
      await this.modelClass.beforeSaveHook(
        apiClientV2,
        data,
        hookOptions.beforeSaveOptions
      )
    }
    if (isUpdate) {
      response = await axios.patch(
        constructUpdateUrl(this.modelClass, data.id, data),
        data,
        config
      )
    } else {
      delete data.id
      response = await axios.post(
        constructCreateUrl(this.modelClass, data),
        data,
        axiosConfig
      )
      // store id of created object in data, such that it is available in the form component
      if ((response.data as any).id) {
        data.id = (response.data as any).id
      }
    }
    if (this.modelClass.afterSaveHook) {
      await this.modelClass.afterSaveHook(
        apiClientV2,
        data,
        hookOptions.afterSaveOptions,
        response.data
      )
    }
    await this.refresh()
    return response
  }

  /**
   * Update existing object and refresh subscribers
   */
  public async update(
    data: ModelClass,
    config: AxiosRequestConfig = store.state.global.axiosConfig,
    hookOptions?: Collection.HookOptions
  ): Promise<AxiosResponse<K>> {
    return this.create(data, config, true, hookOptions)
  }

  public delete(
    id: string,
    purge: boolean = false,
    config: AxiosRequestConfig = store.state.global.axiosConfig
  ): Promise<AxiosResponse<any>> {
    const url = constructDeleteUrl(this.modelClass, id, purge)
    return axios
      .delete(url, config)
      .then((response) => {
        this.refresh()
        return response
      })
      .catch((error) => {
        console.warn(error)
        return Promise.reject(error)
      })
  }

  refresh() {
    this.subscriptionsMap.forEach((apiListSubscription) => {
      apiListSubscription.refresh()
    })
  }
}

export interface Context {
  filter: Collection.Filter
  pagination: Collection.Pagination
}

interface ListResponse<T = any> {
  size: number
  page: number
  page_size: number
  results: T[]
}

abstract class ApiSubscription<T> {
  axiosConfig: AxiosRequestConfig
  modelClass: TransientBaseObjectType
  status: Ref<Collection.Status>
  periodicRefreshObservable: Observable<any>
  subscription: Subscription

  constructor(
    axiosConfig: AxiosRequestConfig,
    modelClass: TransientBaseObjectType
  ) {
    this.axiosConfig = axiosConfig
    this.modelClass = modelClass

    this.status = ref({
      fetching: false,
      ready: false,
      readyPromise: Promise.resolve(true),
    })
  }

  /**
   * Disposes the subscription
   */
  dispose(): void {
    this.subscription.unsubscribe()
  }
}

export class ApiSingleSubscription<T> extends ApiSubscription<T> {
  id: string
  refreshObservable: Observable<T>
  object: T = {} as T

  constructor(
    axiosConfig: AxiosRequestConfig,
    modelClass: TransientBaseObjectType,
    id: string,
    refreshPeriod?: number
  ) {
    super(axiosConfig, modelClass)
    this.id = id

    // Setup input streams
    if (refreshPeriod) {
      // Periodic refresh stream: Periodic refresh stream
      this.periodicRefreshObservable = timer(0, refreshPeriod).pipe(
        map(() => this.id)
      )
    } else {
      this.periodicRefreshObservable = from([this.id])
    }

    this.refreshObservable = combineLatest(this.periodicRefreshObservable).pipe(
      flatMap(() => {
        return from(this.getObject(this.id))
      }),
      share()
    )

    this.subscription = this.refreshObservable.subscribe((objectResponse) => {
      if (objectResponse) {
        // TODO migrate to Vue 3:
        // OLD: Vue.set(this, 'object', objectResponse)
        this.object = objectResponse
      }
    })
  }

  private getObject(id: string): Promise<T> {
    // If filter value is 'null' or '0', we return the default object.
    if (id === null || id === '0') {
      return Promise.resolve(this.modelClass.defaultModel)
    }

    this.status.value.fetching = true
    return apiClientV2
      .get<T>(this.modelClass, id)
      .then((response) => {
        this.status.value.fetching = false
        return response
      })
      .catch((error) => {
        this.status.value.fetching = false
        // TODO: Create errorStream and push there
        return this.modelClass.defaultModel
      })
  }
}

export class ApiListSubscription<T> extends ApiSubscription<T> {
  key: string
  info: Ref<Collection.Info>
  annotations: Collection.Annotation<TransientBaseObject, T>[]
  joins: Collection.RelatedAnnotation<TransientBaseObject>[]
  filter: Collection.Filter
  pagination: Collection.Pagination
  url: string
  objects: Ref<TransientBaseObjectType[]> = ref([])

  contextSubject: BehaviorSubject<Context>
  contextObservable: Observable<Context>
  refreshObservable: Observable<ListResponse>

  constructor(
    axiosConfig: AxiosRequestConfig,
    modelClass: TransientBaseObjectType,
    filter: Collection.Filter = null,
    pagination: Collection.Pagination = null,
    annotations: Collection.Annotation<TransientBaseObject, T>[] = [],
    joins: Collection.RelatedAnnotation<TransientBaseObject>[] = [],
    refreshPeriod?: number
  ) {
    super(axiosConfig, modelClass)
    this.filter = filter
    this.pagination = pagination
    this.info = ref({ size: 0 })
    this.url = constructUrl(modelClass)
    this.key = stringify({
      filter,
      pagination,
      joins,
    })
    // TODO: Check that we dont have duplicates.
    // passed annotations & joins have higher importance than modelClass things
    this.annotations = annotations.concat(modelClass.annotations)
    this.joins = joins.concat(modelClass.joins)

    // Setup input streams
    if (refreshPeriod) {
      // Periodic refresh stream: Periodic refresh stream
      this.periodicRefreshObservable = timer(0, refreshPeriod).pipe(
        map(() => this)
      )
    } else {
      this.periodicRefreshObservable = from([null])
    }

    this.contextSubject = new BehaviorSubject({
      filter: this.filter,
      pagination: this.pagination,
    })
    this.contextObservable = this.contextSubject.asObservable()
    this.refreshObservable = combineLatest(
      this.contextObservable,
      this.periodicRefreshObservable
    ).pipe(
      map(([context, refresh]) => {
        return context
      }),
      flatMap((context: Context) => {
        return from(
          this.getList(context).catch((err) => {
            // catch error here to not break rxjs flow
            console.warn(err)
            return {
              size: 0,
              page: 1,
              page_size: 1,
              results: [],
            }
          })
        )
      }),
      share()
    )

    // RefreshObservable emits the list of objects from the backend
    // * every time the context (filter or pagination) changes or
    // * when the periodicRefreshObservable (a timer) emits
    // This list is then already passed to the components via this.objects
    // Annotations and joins are filled asynchronously below
    this.subscription = this.refreshObservable.subscribe(
      (listResponse: ListResponse) => {
        // TODO: Use DataStateDisplay.Loading and DataStateDisplay.Error for annotations
        this.info.value.size = listResponse.size
        // For join data that add new properties to object, we need to add the properties here already so that they are reactive later
        this.joins.forEach((join) => {
          const relatedObjectType = join.relatedObjectType
            ? join.relatedObjectType
            : join.relatedModelClass.objectType
          const relatedObjectProperty = join.relatedObjectProperty
          const newProperty = `${relatedObjectType}_${relatedObjectProperty}`
          listResponse.results.forEach((object) => {
            object[newProperty] = DataStateDisplay.Loading
          })
        })
        this.annotations.forEach((annotation) => {
          listResponse.results.forEach((object) => {
            object[annotation.key] = DataStateDisplay.Loading
          })
        })
        this.objects.value = listResponse.results
      }
    )

    // Get annotations by calling the callback function for each object and each annotation
    // Then emitting all annotation responses as a stream
    const annotater = this.refreshObservable.pipe(
      flatMap((response) => {
        const annotationSubject = new Subject<
          Collection.AnnotationResponse<T>
        >()
        response.results.forEach((object) => {
          this.annotations.forEach((annotation) => {
            annotation
              .callback(object, apiClientV2)
              .then((annotationResponse) => {
                annotationSubject.next(annotationResponse)
              })
          })
        })
        return annotationSubject
      })
    )

    // Keep map of joined objects so that each object only needs one request
    const joinMap = {}

    // Get joins (related annotations) by getting the related object and extracting the related property
    // for each object and each join
    // Then emitting all annotation responses as a stream
    const joiner = this.refreshObservable.pipe(
      flatMap((response) => {
        const annotationSubject = new Subject<
          Collection.AnnotationResponse<T>
        >()
        response.results.forEach((object) => {
          this.joins.forEach((join) => {
            const relatedObjectType =
              join.relatedObjectType || join.relatedModelClass.objectType
            const relatedModelClass =
              join.relatedModelClass || getModelClass(join.relatedObjectType)
            const relatedObjectProperty = join.relatedObjectProperty
            const newProperty = `${relatedObjectType}_${relatedObjectProperty}`
            const annotations = {
              id: object.id,
              annotations: {
                [newProperty]: null,
              },
            }
            if (object[relatedObjectType]) {
              // Add the object type to the join map
              if (!joinMap[relatedModelClass.objectType]) {
                joinMap[relatedModelClass.objectType] = {}
              }

              // Add the object to the join map
              if (
                !joinMap[relatedModelClass.objectType][
                  object[relatedObjectType]
                ]
              ) {
                const getPromise = apiClientV2.get(
                  relatedModelClass,
                  object[relatedObjectType]
                )
                joinMap[relatedModelClass.objectType][
                  object[relatedObjectType]
                ] = getPromise
              }
              joinMap[relatedModelClass.objectType][
                object[relatedObjectType]
              ].then((response) => {
                annotations.annotations[newProperty] =
                  response[relatedObjectProperty]
                annotationSubject.next(annotations)
              })
            } else if (object[relatedObjectType] === null) {
              annotations.annotations[newProperty] = DataStateDisplay.OkNoData
              annotationSubject.next(annotations)
            } else {
              throw new Error(`${relatedObjectType} does not exist on object.`)
            }
          })
        })
        return annotationSubject
      })
    )

    // Combine joins and annotations and insert into objects
    const annotaterSubscription = merge(joiner, annotater).subscribe(
      (annotation) => {
        annotateObject(this.objects.value, annotation)
      }
    )
    this.subscription.add(annotaterSubscription)

    function annotateObject(
      objects: TransientBaseObject[],
      annotation: Collection.AnnotationResponse<T>
    ) {
      if (annotation.id) {
        const index = objects.findIndex((object) => {
          return object.id === annotation.id
        })
        if (index >= 0) {
          insertPropsInObject(objects[index], { ...annotation.annotations })
        }
      }
    }
  }

  /**
   * Set collection filter. Replaces all filter properties.
   * @param {CollectionFilter} filter: When null, the collection is emptied.
   */
  public setFilter(
    filter: Collection.Filter | null,
    noRefresh: boolean = false
  ): Promise<boolean> {
    if (isEmpty(filter)) {
      filter = null
    }

    this.filter = filter
    if (!noRefresh) {
      return this.refresh()
    }
  }

  /**
   * Update collection filter. Filter properties that are updated to undefined are deleted
   * from the filter. Set to a filter property can be set to null.
   * @param {CollectionFilter} filter: The filter properties to update.
   */
  public updateFilter(filter: Collection.Filter): Promise<void> {
    // TODO
    return
  }

  /**
   * Refreshes collection.
   * @returns {Promise<boolean>}
   */
  public refresh(): Promise<boolean> {
    this.contextSubject.next({
      filter: this.filter,
      pagination: this.pagination,
    })
    return Promise.resolve(true)
  }

  private getList(context: Context): Promise<ListResponse> {
    // TODO: Check if all required filters and pagination is given

    // If filter value is 'null', we leave the collection empty.
    if (context.filter === null) {
      return Promise.resolve({
        size: 0,
        page: 1,
        page_size: 1,
        results: [],
      })
    }

    this.status.value.fetching = true
    return axios
      .get(constructListUrl(this.url, context), this.axiosConfig)
      .then((response) => {
        this.status.value.fetching = false
        return {
          ...response.data,
        }
      })
      .catch((error) => {
        this.status.value.fetching = false
        return Promise.reject(error)
      })
  }

  /**
   * Set collection pagination.
   * @param {number} page
   * @param {number} pageSize
   * @returns {void}
   */
  setPagination(page?: number, pageSize?: number): Promise<boolean> {
    if (page) {
      this.pagination.page = page
    }
    if (pageSize) {
      this.pagination.pageSize = pageSize
    }
    return this.refresh()
  }

  /**
   * Set collection order.
   * @param {string} field
   * @param {string} order
   * @returns {Promise<boolean>}
   */
  setOrder(field: string, order?: string): Promise<boolean> {
    if (order) {
      if (order === 'desc' && !field.endsWith('_dsc')) {
        if (!field.endsWith('_asc')) {
          // Add _dsc
          this.filter.order_by = field + '_dsc'
        } else {
          // Slice off _asc and add _dsc:
          this.filter.order_by = field.slice(0, -4) + '_dsc'
        }
      } else {
        this.filter.order_by = field
      }
    } else {
      this.filter.order_by = field
    }
    return this.refresh()
  }

  public setFilterAndPagination(
    filter: Collection.Filter,
    pagination: Collection.Pagination
  ) {
    // Only need to refresh if something changed
    this.filter = filter
    if (pagination.page) {
      this.pagination.page = pagination.page
    }
    if (pagination.pageSize) {
      this.pagination.pageSize = pagination.pageSize
    }
    return this.refresh()
  }
}

export class ApiClientV2 {
  axiosConfig: AxiosRequestConfig = undefined
  collections = new Map<string, Collection<ModelClass, ModelClass>>()

  private refreshSubject = new Subject<void>()

  constructor(axiosConfig?: AxiosRequestConfig) {
    if (axiosConfig) {
      this.axiosConfig = JSON.parse(JSON.stringify(axiosConfig))
    }
    // MockHandler.getInstance(axios)
  }

  /**
   * Get a single object
   * @param modelClass
   * @param id
   */
  public get<T>(modelClass: TransientBaseObjectType, id: string): Promise<T> {
    if (id === undefined || id === null || id === '') {
      return Promise.resolve({} as T)
    }
    if (id === '0') {
      return Promise.resolve(modelClass.defaultModel)
    }
    const collection = this.getOrCreateCollection(modelClass)
    return collection.get(id, this.axiosConfig)
  }

  /**
   * Get list of objects as ListResponse
   * @param modelClass
   * @param context
   */
  public async getList<T>(
    modelClass: TransientBaseObjectType,
    context: Context
  ): Promise<ListResponse<T>> {
    const collection = this.getOrCreateCollection(modelClass)
    return collection.getList<T>(context, this.axiosConfig)
  }

  /**
   * Get list of objects as array
   * @param modelClass
   * @param context
   */
  public async getListItems<T>(
    modelClass: TransientBaseObjectType,
    context: Context
  ): Promise<T[]> {
    const response = await this.getList<T>(modelClass, context)
    return response.results
  }

  /**
   * Get or create a single object
   * @param modelClass
   * @param data
   * @param find_by keys of data used to find object
   */
  public async getOrCreate<T>(
    modelClass: TransientBaseObjectType,
    data: any,
    find_by: string[]
  ): Promise<T> {
    try {
      const query = {}
      find_by.forEach((key) => {
        query[key] = data[key]
      })
      // explicitly return await to catch 404
      return await this.find<T>(modelClass, query)
    } catch (error) {
      if (error.response && error.response.status === 404) {
        return this.create<T, T>(modelClass, data)
      } else {
        return Promise.reject(error)
      }
    }
  }

  /**
   * Custom GET for special URLs
   * @param url
   * @param query
   * @param extraConfig Will be added to existing request config, overwriting keys that are already there
   */
  public async customGet(
    url: string,
    query: object = {},
    extraConfig?
  ): Promise<any> {
    url = BASE_URL + url + '?'
    for (const key in query) {
      url += '&' + key + '=' + query[key]
    }
    let axiosConfig = simpleCopy(this.axiosConfig)
    if (extraConfig) {
      axiosConfig = {
        ...axiosConfig,
        ...extraConfig,
      }
    }
    const response = await axios.get(url, axiosConfig)
    return response.data
  }

  /**
   * Custom POST for special URLs
   */
  public async customPost(
    url: string,
    data: object = {},
    headers?: { [key: string]: string }
  ): Promise<any> {
    url = BASE_URL + url
    const axiosConfig = simpleCopy(this.axiosConfig)
    if (headers) {
      // copy config to not modify original one
      if (!axiosConfig.headers) {
        axiosConfig.headers = {}
      }
      for (const key in headers) {
        axiosConfig.headers[key] = headers[key]
      }
    }
    return axios.post(url, data, axiosConfig).then((response) => response.data)
  }

  /**
   * Custom DELETE for special URLs
   */
  public async customDelete(url: string): Promise<any> {
    url = BASE_URL + url
    return axios.delete(url, this.axiosConfig).then((response) => response.data)
  }

  /**
   * Custom PUT for special URLs
   */
  public async customPut(url: string, data: object = {}): Promise<any> {
    url = BASE_URL + url
    return axios
      .put(url, data, this.axiosConfig)
      .then((response) => response.data)
  }

  public async find<T>(modelClass: ModelClass, query: object = {}): Promise<T> {
    let url = constructFindUrl(modelClass)
    for (const key in query) {
      url += '&' + key + '=' + query[key]
    }
    const response = await axios.get(url, this.axiosConfig)
    return response.data as T
  }

  /**
   * Create new object of type modelClass
   * @param {ModelClass} modelClass
   * @param data
   * @returns {Promise<K>}
   */
  public create<T, K>(modelClass: ModelClass, data: T): Promise<K> {
    const collection: Collection<T, K> = this.getOrCreateCollection(modelClass)

    return collection
      .create(data, this.axiosConfig)
      .then((response) => response.data)
  }

  /**
   * Update object of type modelClass
   * @param {ModelClass} modelClass
   * @param {T} data
   * @returns {Promise<K>}
   */
  public update<T, K>(
    modelClass: ModelClass,
    data: T,
    hookOptions?: Collection.HookOptions
  ): Promise<K> {
    const collection: Collection<T, K> = this.getOrCreateCollection(modelClass)

    return collection
      .update(data, this.axiosConfig, hookOptions)
      .then((response) => response.data)
  }

  /**
   * Delete a single object
   * @param {ModelClass} modelClass
   * @param {string} id
   * @param {boolean} purge
   */
  public delete(
    modelClass: ModelClass,
    id: string,
    purge: boolean = false
  ): Promise<void> {
    const collection = this.getOrCreateCollection(modelClass)

    return collection.delete(id, purge, this.axiosConfig)
  }

  public publish(modelClass: ModelClass, id: string) {
    const url = `${modelClass.apiUrl}/${id}/publish`
    return this.customPost(url, undefined)
  }

  // Function that gets called for each object in collection
  public annotate<T, K>(
    object: T,
    annotation: Collection.Annotation<T, K>
  ): Promise<Collection.AnnotationResponse<K>> {
    return annotation.callback(object, new ApiClientV2())
  }

  public subscribe<T, K>(
    modelClass: ModelClass,
    filter: Collection.Filter,
    pagination: Collection.Pagination,
    annotations?: Collection.Annotation<TransientBaseObject, K>[],
    joins?: Collection.RelatedAnnotation<TransientBaseObject>[],
    refreshPeriod?: number
  ): ApiListSubscription<K> {
    // Get or create collection and add new subscriber
    const collection: Collection<T, K> = this.getOrCreateCollection(modelClass)
    const apiListSubscription: ApiListSubscription<K> = collection.subscribe(
      this.axiosConfig,
      modelClass,
      filter,
      pagination,
      annotations,
      joins,
      refreshPeriod
    )
    /* TODO: Fix this:
    if (vm) {
      vm.$on('hook:destroyed', () => {
        apiListSubscription.dispose()
      })
    }
    */
    return apiListSubscription
  }

  private getOrCreateCollection<T, K>(
    modelClass: ModelClass
  ): Collection<T, K> {
    let collection = this.collections.get(modelClass.objectType)
    if (!collection) {
      collection = new Collection(modelClass)
      this.collections.set(modelClass.objectType, collection)
    }
    return collection
  }

  public getAttachmentUrl(id: string) {
    if (id === undefined || id === null) {
      return
    }
    return `${BASE_URL}attachment/${id}/content?${Math.random()}`
  }

  public refreshCollections() {
    const promises = []
    this.collections.forEach((collection: Collection<any, any>) => {
      promises.push(collection.refresh())
    })
    return Promise.all(promises)
  }

  public refreshData() {
    this.refreshSubject.next()
    return this.refreshCollections()
  }

  public getRefreshStream(): Observable<void> {
    return this.refreshSubject.asObservable()
  }
}

export const apiClientV2 = new ApiClientV2()
